import React from 'react';

import { DefaultContext } from '@apollo/client';
import { useSnackbar } from 'notistack';

import {
  DEFAULT_ALERT_AUTO_HIDE_DURATION,
  DEFAULT_ALERT_TITLES,
  SnackMessage,
  SnackVariantKey,
} from '@clearverify/constants';

import type { SnackMessageCreateFn } from 'src/types/snackbar';

export const useGenericNotification = (contentCallback: SnackMessageCreateFn) => {
  const { enqueueSnackbar } = useSnackbar();

  const spawnNotification = React.useCallback(
    (
      text: React.ReactNode,
      variant: SnackVariantKey = 'info',
      title?: React.ReactNode,
      autoHideDuration = DEFAULT_ALERT_AUTO_HIDE_DURATION,
    ) => {
      enqueueSnackbar(text, {
        autoHideDuration,
        content: contentCallback(variant, title ?? DEFAULT_ALERT_TITLES[variant]),
      });
    },
    [contentCallback, enqueueSnackbar],
  );

  const createApolloContext = React.useCallback(
    (params?: { errorMessage?: string; successMessage?: string }): DefaultContext => {
      if (!params || Object.keys(params).length === 0) {
        console.info('createApolloContext was called empty');
      }

      return {
        [SnackMessage.error]: params?.errorMessage,
        ...(params?.successMessage
          ? {
              [SnackMessage.success]: params.successMessage,
            }
          : {}),
      };
    },
    [],
  );

  const apolloContextValues = React.useMemo(() => {
    return {
      error: SnackMessage.error,
      success: SnackMessage.success,
    };
  }, []);

  return { spawnNotification, createApolloContext, apolloContextValues };
};
